import * as i0 from '@angular/core';
import { Injectable, Inject, Component, Input, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ReplaySubject, map, tap, BehaviorSubject, switchMap, of, catchError, from, defer, mergeMap, firstValueFrom, filter, first, delay, throwError } from 'rxjs';
import { UssUrlRewriter, UssOidcManager, UssJwtDecoder } from '@uofu-uss/uss-oidc';

/**
 * defines all allowed bases.
 */
var AppEnv;
(function (AppEnv) {
    AppEnv["local"] = "local";
    AppEnv["dev"] = "dev";
    AppEnv["test"] = "test";
    AppEnv["qa"] = "qa";
    AppEnv["disaster_recovery"] = "disaster_recovery";
    AppEnv["prod"] = "prod";
})(AppEnv || (AppEnv = {}));
var AlternateEnvironment;
(function (AlternateEnvironment) {
    AlternateEnvironment["unstable"] = "unstable";
    AlternateEnvironment["poc"] = "poc";
})(AlternateEnvironment || (AlternateEnvironment = {}));
function generateGatewayEnvironmentConfig(appUrl, clientId, clientSecret) {
    return {
        appUrl,
        clientId,
        clientSecret,
    };
}
function generateEmptyEnvironmentConfig() {
    return generateGatewayEnvironmentConfig('', '', '');
}
class ApiConfigService {
    constructor() {
        this.apiConfigSubject$ = new ReplaySubject(1);
        this.credentialsByApp = ApiConfigService.emptyEnvironmentDetails;
        this.gatewayApiPaths = [];
        this.alternateEnvironment = undefined;
        this.baseConfig = undefined;
        this.initialized = false;
    }
    static { this.emptyEnvironmentDetails = {
        local: generateEmptyEnvironmentConfig(),
        dev: generateEmptyEnvironmentConfig(),
        test: generateEmptyEnvironmentConfig(),
        qa: generateEmptyEnvironmentConfig(),
        disaster_recovery: generateEmptyEnvironmentConfig(),
        prod: generateEmptyEnvironmentConfig(),
    }; }
    /**
     * returns an observable for the api configuration
     * only emits once the configuration is initialized
     * backed by a ReplaySubject so there can be multiple subscribers
     * don't want to return the subject directly to prevent tampering
     */
    get apiConfig$() {
        return this.apiConfigSubject$;
    }
    /**
     * initializes the service with the provided configuration
     *
     * @param apiConfig
     */
    initialize(apiConfig) {
        if (this.initialized) {
            console.warn("ApiConfigService was initialized more than once! This may cause unexpected application issues or instability.");
        }
        Object.assign(this, { ...apiConfig, initialized: true });
        this.apiConfigSubject$.next(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: ApiConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: ApiConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: ApiConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

function generateBaseGatewayUrl(gateway) {
    return UssUrlRewriter.getGatewayApiUrl(gateway);
}
class GatewayUrlService {
    static { this.gatewayBaseUrl = generateBaseGatewayUrl('stable'); }
    static { this.gatewayDevBaseUrl = generateBaseGatewayUrl('dev'); }
    static { this.gatewayUnstableBaseUrl = generateBaseGatewayUrl('unstable'); }
    static { this.gatewayPocBaseUrl = generateBaseGatewayUrl('poc'); }
    static { this.gatewayDisasterRecoveryBaseUrl = generateBaseGatewayUrl('disaster_recovery'); }
    static { this.applicationEnvironmentToGateway = {
        local: GatewayUrlService.gatewayDevBaseUrl,
        dev: GatewayUrlService.gatewayDevBaseUrl,
        prod: GatewayUrlService.gatewayBaseUrl,
        qa: GatewayUrlService.gatewayDevBaseUrl,
        test: GatewayUrlService.gatewayDevBaseUrl,
        disaster_recovery: GatewayUrlService.gatewayDisasterRecoveryBaseUrl,
    }; }
    static { this.defaultEnvironmentSubstitutions = {
        local: "-dev",
        dev: "-dev",
        prod: "",
        qa: "-qa",
        test: "-test",
        disaster_recovery: "",
    }; }
    /**
     * The substitution string that may be used to specify the environment.
     */
    static { this.envReplacement = '{env}'; }
    constructor(document, apiConfigService) {
        this.document = document;
        this.apiConfigService = apiConfigService;
    }
    /**
     * Used to add an API URL mapping for use with the API gateway.
     * @param given - the API to define
     */
    addApiUrl(given) {
        const updated = this.apiConfigService.gatewayApiPaths.slice();
        updated.push(given);
        this.apiConfigService.gatewayApiPaths = updated;
    }
    clearApiUrls() {
        this.apiConfigService.gatewayApiPaths = [];
    }
    alterRequestForGateway(relativeUrl, envDetails = this.apiConfigService.credentialsByApp, alternativeEnvironment = this.apiConfigService.alternateEnvironment) {
        if (this.isRequestModificationNeeded(relativeUrl)) {
            const appEnv = GatewayUrlService.determineEnv(envDetails, this.documentUrl());
            const gateway = GatewayUrlService.pickGateway(appEnv, alternativeEnvironment);
            const urlInfo = this.findUrlInfo(relativeUrl);
            return this.generateGatewayUrl(appEnv, gateway, relativeUrl, urlInfo);
        }
        else {
            return relativeUrl;
        }
    }
    static determineEnv(environmentDetails, url) {
        const env = Object.keys(environmentDetails)
            .find(env => url.indexOf(environmentDetails[env].appUrl.replace(/\/$/, '')) === 0);
        return env || AppEnv.prod;
    }
    static pickGateway(appEnv, alternativeEnvironment) {
        if ((appEnv === AppEnv.dev || appEnv === AppEnv.local) && alternativeEnvironment) {
            return alternativeEnvironment === AlternateEnvironment.unstable ?
                GatewayUrlService.gatewayUnstableBaseUrl : GatewayUrlService.gatewayPocBaseUrl;
        }
        else {
            return GatewayUrlService.applicationEnvironmentToGateway[appEnv];
        }
    }
    generateGatewayUrl(env, gateway, relativeUrl, urlInfo) {
        const relativeGatewayUrl = relativeUrl.replace(urlInfo.relativeUrl, urlInfo.relativeGatewayUrl);
        const envReplacement = (urlInfo.environmentSubstitutions || GatewayUrlService.defaultEnvironmentSubstitutions)[env];
        const relativeGatewayUrlWithEnv = relativeGatewayUrl.replace(GatewayUrlService.envReplacement, envReplacement);
        return gateway + relativeGatewayUrlWithEnv;
    }
    isGateway(href) {
        return href.indexOf(GatewayUrlService.gatewayBaseUrl) === 0 ||
            href.indexOf(GatewayUrlService.gatewayDevBaseUrl) === 0 ||
            href.indexOf(GatewayUrlService.gatewayUnstableBaseUrl) === 0 ||
            href.indexOf(GatewayUrlService.gatewayPocBaseUrl) === 0 ||
            href.indexOf(GatewayUrlService.gatewayDisasterRecoveryBaseUrl) === 0;
    }
    findUrlInfo(relativeUrl) {
        return this.apiConfigService.gatewayApiPaths
            .find(current => relativeUrl.indexOf(current.relativeUrl) === 0);
    }
    /**
     * Determines if a given relative URL should be altered to work with the gateway.
     *
     * Example 1: Browser url: 'pay-entry.app.dev.utah.edu', Relative url: '/api/foo' -> true
     * Example 2: Browser url: 'localhost:4200', Relative url: '/api/foo' -> true
     */
    isRequestModificationNeeded(relativeUrl) {
        return !!this.findUrlInfo(relativeUrl);
    }
    documentUrl() {
        return this.document.location.href;
    }
    assignUrl(url) {
        this.document.location.assign(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: GatewayUrlService, deps: [{ token: DOCUMENT }, { token: ApiConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: GatewayUrlService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: GatewayUrlService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: ApiConfigService }] });

class GatewayConfigService {
    constructor(gatewayUrlService, apiConfigService) {
        this.gatewayUrlService = gatewayUrlService;
        this.apiConfigService = apiConfigService;
    }
    generateAuthCodeFlowConfig$() {
        return this.apiConfigService.apiConfig$.pipe(map(apiConfig => {
            const currentLocation = this.gatewayUrlService.documentUrl();
            const env = GatewayUrlService.determineEnv(apiConfig.credentialsByApp, currentLocation);
            const credentials = this.findClientCredentials(env, apiConfig);
            const mappedEnv = this.mapEnv(env);
            const config = {
                autoLogin: false,
                autoRevoke: true,
                autoDiscovery: true,
                authority: '',
                client_id: credentials.clientId,
                client_secret: credentials.clientSecret,
                redirect_uri: credentials.appUrl,
                ussEnvironment: mappedEnv,
                maxLoginRetries: 5,
            };
            if (apiConfig.alternateEnvironment && (env === AppEnv.dev || env === AppEnv.local)) {
                config.gateway = apiConfig.alternateEnvironment;
            }
            return Object.assign(config, apiConfig.baseConfig);
        }));
    }
    mapEnv(env) {
        switch (env) {
            case AppEnv.local:
                return 'localhost';
            case AppEnv.dev:
                return 'dev';
            case AppEnv.test:
                return 'test';
            case AppEnv.qa:
                return 'qa';
            case AppEnv.disaster_recovery:
                return 'disaster_recovery';
            default:
                return 'prod';
        }
    }
    findClientCredentials(env, apiConfig) {
        return apiConfig.credentialsByApp[env];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: GatewayConfigService, deps: [{ token: GatewayUrlService }, { token: ApiConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: GatewayConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: GatewayConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: GatewayUrlService }, { type: ApiConfigService }] });

class IdentityClaims {
    constructor(claimObject) {
        this.claimObject = claimObject;
    }
    get displayName() {
        return this.claimObject?.displayName || '';
    }
    get givenName() {
        return this.claimObject?.givenName || '';
    }
    get email() {
        return this.claimObject?.emailAddress || '';
    }
    get unid() {
        return this.claimObject?.unid || '';
    }
    get emplid() {
        return this.claimObject?.emplid || '';
    }
    get eduPersonAffiliation() {
        return this.claimObject?.eduPersonAffiliation || [];
    }
    get roles() {
        return this.claimObject?.roles || [];
    }
}

class OAuthProviderService {
    constructor(gatewayConfigService) {
        this.gatewayConfigService = gatewayConfigService;
        this.ussOidcManager$ = new ReplaySubject(1);
    }
    generateOidcManager$(initialize = false) {
        if (!!this.ussOidcManager) {
            console.warn("OidcManager was generated more than once! This may cause unexpected application issues or instability.");
        }
        return this.gatewayConfigService.generateAuthCodeFlowConfig$().pipe(map(settings => new UssOidcManager(settings, initialize)), tap(oidcManager => {
            this.ussOidcManager = oidcManager;
            this.ussOidcManager$.next(oidcManager);
        }));
    }
    // obfuscates the replay subject as an observable
    get oidcManager$() {
        return this.ussOidcManager$;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: OAuthProviderService, deps: [{ token: GatewayConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: OAuthProviderService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: OAuthProviderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: GatewayConfigService }] });

var LoggedInStatus;
(function (LoggedInStatus) {
    LoggedInStatus[LoggedInStatus["LoggedOut"] = 0] = "LoggedOut";
    LoggedInStatus[LoggedInStatus["LoggedIn"] = 1] = "LoggedIn";
    LoggedInStatus[LoggedInStatus["Unknown"] = 2] = "Unknown";
})(LoggedInStatus || (LoggedInStatus = {}));
class LoginService {
    #user;
    #identityClaims;
    constructor(oauthProviderService, gatewayUrlService) {
        this.oauthProviderService = oauthProviderService;
        this.gatewayUrlService = gatewayUrlService;
        this.myValue = LoggedInStatus.Unknown;
        this.myObservable = new BehaviorSubject(this.myValue);
        this.#user = null;
        this.#identityClaims = new IdentityClaims();
        this.oidcManager$ = oauthProviderService.generateOidcManager$();
    }
    get loggedInStatus() {
        return this.myValue;
    }
    set loggedInStatus(given) {
        this.myValue = given;
        this.myObservable.next(this.myValue);
    }
    get loggedInStatusObservable() {
        return this.myObservable;
    }
    get accessToken() {
        return this.#user?.access_token || '';
    }
    get identityToken() {
        return this.#user?.id_token || '';
    }
    get identityClaims() {
        return this.#identityClaims;
    }
    get grantedScopes() {
        return this.#user?.scopes || [];
    }
    get loggedIn() {
        return this.loggedInStatus === LoggedInStatus.LoggedIn;
    }
    /**
     * @param forceReload - force a fresh login, regardless of current login status and state stored on the client.
     */
    login(forceReload = false) {
        return this.oidcManager$.pipe(mergeMap(oidcManager => {
            return defer(() => from(forceReload ? this.clearOldLogin() : Promise.resolve()))
                .pipe(switchMap(() => oidcManager.checkLogin(true, oidcManager.userManager)), switchMap((success) => success ? oidcManager.userManager.getUser() : Promise.resolve(null)), catchError((err) => {
                console.log(`Login failed: ${err}`);
                return of(null);
            }), tap((user) => {
                const encodedToken = user?.id_token || '';
                const rawIdToken = encodedToken ? new UssJwtDecoder(encodedToken).idToken : undefined;
                this.#identityClaims = new IdentityClaims(rawIdToken);
                this.#user = user;
                this.loggedInStatus = encodedToken ? LoggedInStatus.LoggedIn : LoggedInStatus.LoggedOut;
            }), map(() => {
                return this.loggedInStatus;
            }));
        }));
    }
    clearOldLogin() {
        return firstValueFrom(this.oidcManager$.pipe(mergeMap(oidcManager => {
            return oidcManager.userManager.clearStaleState()
                .then(() => oidcManager.userManager.removeUser())
                .catch((reason) => {
                console.error(`failed to clear old user information: ${reason}`);
                return Promise.resolve();
            });
        })));
    }
    logout() {
        return this.oidcManager$.pipe(mergeMap(oidcManager => from(oidcManager.userManager.signoutRedirect())), map(() => LoggedInStatus.LoggedOut), tap(status => this.clearLogin(status)));
    }
    silentLogout(redirectLocation) {
        return defer(() => this.safeSilentLogout())
            .pipe(tap(status => {
            this.clearLogin(status);
            if (redirectLocation) {
                this.gatewayUrlService.assignUrl(redirectLocation);
            }
        }));
    }
    clearLogin(status) {
        this.#identityClaims = new IdentityClaims();
        this.#user = null;
        this.loggedInStatus = status;
    }
    safeSilentLogout() {
        return this.oidcManager$.pipe(mergeMap(oidcManager => oidcManager.userManager.signoutSilent()), map(() => LoggedInStatus.LoggedOut), catchError((error) => {
            console.error(`unable to log out with WSO2 from logout component: ${error}`);
            return of(LoggedInStatus.Unknown);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: LoginService, deps: [{ token: OAuthProviderService }, { token: GatewayUrlService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: LoginService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: LoginService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: OAuthProviderService }, { type: GatewayUrlService }] });

/**
 * uss-login component
 *
 * takes in two possible values, but one and only ONE should be provided:
 *
 * -- apiConfig: the full api configuration as recognized by ngx-auth
 * -- settings: the base uss-oidc settings
 *
 * Use of UssOidcSettings is strongly discouraged as this exposes the underlying OAuth2 library.
 * This may be removed in the future and is mostly defined to allow for backwards compatability
 * with the ngx-components uofu-login component and easier testing of various configuration with WSO2.
 */
class UssLoginComponent {
    constructor(apiConfigService, oAuthProviderService) {
        this.apiConfigService = apiConfigService;
        this.oAuthProviderService = oAuthProviderService;
    }
    ngOnInit() {
        this.initiateSettings();
        this.oAuthProviderService.generateOidcManager$(true).subscribe(() => {
            // generating the oidc manager will initiate auto-login if enabled, as well as making the LoginService available for manual login/logout
            console.log("ngx-auth: oidc manager initialized");
        });
    }
    initiateSettings() {
        if (!!this.apiConfig && !!this.settings) {
            console.warn("uss-login component: both api config and uss oidc settings were provided, using api config");
        }
        if (!!this.apiConfig) {
            this.apiConfigService.initialize(this.apiConfig);
            return;
        }
        if (!!this.settings) {
            this.apiConfigService.initialize({
                baseConfig: this.settings
            });
            return;
        }
        console.error('neither api config or uss oidc settings provided, unable to initialize uss-login component');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: UssLoginComponent, deps: [{ token: ApiConfigService }, { token: OAuthProviderService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.10", type: UssLoginComponent, selector: "uss-login", inputs: { apiConfig: "apiConfig", settings: "settings" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: UssLoginComponent, decorators: [{
            type: Component,
            args: [{ selector: 'uss-login', template: '' }]
        }], ctorParameters: () => [{ type: ApiConfigService }, { type: OAuthProviderService }], propDecorators: { apiConfig: [{
                type: Input
            }], settings: [{
                type: Input
            }] } });

/**
 * this factory takes in the api configuration and creates a new instance of the ApiConfigService initialized with that config
 * @param apiConfig
 * @returns apiConfigServiceFactory
 */
function getApiConfigServiceFactory(apiConfig) {
    return () => {
        const apiConfigService = new ApiConfigService();
        apiConfigService.initialize(apiConfig);
        return apiConfigService;
    };
}

class AuthModule {
    static forRoot(apiConfig) {
        return {
            ngModule: AuthModule,
            providers: [
                LoginService,
                GatewayUrlService,
                GatewayConfigService,
                {
                    provide: ApiConfigService,
                    useFactory: getApiConfigServiceFactory(apiConfig),
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.10", ngImport: i0, type: AuthModule, declarations: [UssLoginComponent], exports: [UssLoginComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: AuthModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        UssLoginComponent
                    ],
                    exports: [
                        UssLoginComponent
                    ]
                }]
        }] });

class AuthenticatedGuard {
    constructor(loginService) {
        this.loginService = loginService;
    }
    canActivate(next, state) {
        return this.loginService.loggedInStatusObservable
            .pipe(filter((ls) => ls !== LoggedInStatus.Unknown), map(ls => ls === LoggedInStatus.LoggedIn), first());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: AuthenticatedGuard, deps: [{ token: LoginService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: AuthenticatedGuard, providedIn: AuthModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: AuthenticatedGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: AuthModule
                }]
        }], ctorParameters: () => [{ type: LoginService }] });

class ExpiredAuthInterceptor {
    constructor(loginService, gatewayUrlService, apiConfigService) {
        this.loginService = loginService;
        this.gatewayUrlService = gatewayUrlService;
        this.apiConfigService = apiConfigService;
    }
    intercept(request, next) {
        return next.handle(request)
            .pipe(catchError((response) => {
            const apiRequestMatch = !!this.apiConfigService.gatewayApiPaths
                .map(apiPath => apiPath.relativeUrl)
                .map(relativeUrl => {
                return {
                    relativeUrl: relativeUrl,
                    finalUrl: this.gatewayUrlService.alterRequestForGateway(relativeUrl)
                };
            })
                .find(paths => {
                console.debug(`final '${paths.finalUrl}' relative '${paths.relativeUrl}' url '${request.url}'`);
                return request.url.indexOf(paths.finalUrl) === 0 || request.url.indexOf(paths.relativeUrl) === 0;
            });
            if (apiRequestMatch && response.status === 401) {
                this.loginService.loggedInStatus = LoggedInStatus.LoggedOut;
                // Wait 2 seconds before logging in to allow the user to read the warning
                return of(true)
                    .pipe(delay(2000), switchMap(() => this.loginService.login(true)), delay(10000), switchMap(() => throwError(() => response)));
            }
            else {
                throw response;
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: ExpiredAuthInterceptor, deps: [{ token: LoginService }, { token: GatewayUrlService }, { token: ApiConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: ExpiredAuthInterceptor, providedIn: AuthModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: ExpiredAuthInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: AuthModule
                }]
        }], ctorParameters: () => [{ type: LoginService }, { type: GatewayUrlService }, { type: ApiConfigService }] });

class UrlInterceptor {
    constructor(gatewayUrlService, apiConfigService, loginService) {
        this.gatewayUrlService = gatewayUrlService;
        this.apiConfigService = apiConfigService;
        this.loginService = loginService;
    }
    intercept(request, next) {
        //  set gateway URL with env
        if (this.loginService.loggedIn && this.gatewayUrlService.isRequestModificationNeeded(request.url)) {
            const gatewayUrl = this.gatewayUrlService.alterRequestForGateway(request.url);
            console.debug(`updating request URL for gateway from ${request.url} to ${gatewayUrl}`);
            const token = this.loginService.accessToken;
            const headers = token ? request.headers.set('Authorization', `Bearer ${token}`) : request.headers;
            request = request.clone({
                url: gatewayUrl,
                headers,
            });
        }
        return next.handle(request);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: UrlInterceptor, deps: [{ token: GatewayUrlService }, { token: ApiConfigService }, { token: LoginService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: UrlInterceptor, providedIn: AuthModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: UrlInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: AuthModule
                }]
        }], ctorParameters: () => [{ type: GatewayUrlService }, { type: ApiConfigService }, { type: LoginService }] });

/*
 * Public API Surface
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AlternateEnvironment, ApiConfigService, AppEnv, AuthModule, AuthenticatedGuard, ExpiredAuthInterceptor, GatewayConfigService, GatewayUrlService, IdentityClaims, LoggedInStatus, LoginService, UrlInterceptor, UssLoginComponent, generateEmptyEnvironmentConfig, generateGatewayEnvironmentConfig };
